import { useState } from 'react';
import Image from 'next/image';
import styled from 'styled-components';

import classNames from 'classnames';
import { getComponentMargin, getAnchorId } from '@/utils/builderUtils';

import beTwMerge from '@/utils/beTwMerge';
import { Action } from '@/components/SingleImageTile/SingleImageTile';
import Button from '@/components/homepage/Button/Button';
import { moreDescriptiveAriaLabel } from '@/utils/utils';
import DropdownButton from '@/components/homepage/DropdownButton';
import RichText from '../../RichText/RichText';
import Title from '../../Title/Title';

interface ITitle {
  content: string;
  titleHTMLTag?: 'h1' | 'h2' | 'h3';
  titleSize?: 'extraLarge' | 'large' | 'medium' | 'small';
  titleWeight?: 'light' | 'normal' | 'medium' | 'bold' | 'black';
}

interface IDiamondClarityDiagramProps {
  /**
   * Optional large header.
   * - content*: The content of the header
   * - titleHTMLTag: Heading level that will be used to render the optional header. Defaults to 'h2'
   * - titleSize: Select one of the styles 'extraLarge' | 'large' | 'medium' | 'small'.
   * - titleWeight: Optional header weight. Defaults to 'normal'
   */
  optionalLargeHeader?: ITitle;
  /**
   * Turn on to add margin to the bottom of the component
   */
  margin?: boolean;
  /**
   * Give to the component an anchor link id
   */
  anchorLinkID?: string;
  /**
   * To position the content either to the left or to the right
   */
  contentPosition: string;
  /**
   * To position the image either to the center or to the start
   */
  mediaAlignment: 'start' | 'center';
  /**
   *  Rich text content of the Block
   */
  content: string;
  /**
   * The list of cta buttons
   * Props.
   * - text*: Text of the button
   * - url*: url to link to
   * - newTab*: If the link should open in a new tab
   */
  actions?: Action[];
}

const Container = styled.div`
  width: 100%;
  max-width: 500px;

  .col-md-6.col-md-pull-6 {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-size: 14px;
    line-height: 1.428571429;
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    box-sizing: border-box;
    max-height: 999999px;
    position: relative;
    min-height: 1px;
    padding-left: 0;
    padding-right: 0;
  }

  .search-noUiSlider-wrap {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-size: 14px;
    line-height: 1.428571429;
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    box-sizing: border-box;
    max-height: 999999px;
    padding: 0 5%;
  }

  .clarity-scale {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-size: 14px;
    line-height: 1.428571429;
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    box-sizing: border-box;
    max-height: 999999px;
    padding: 5% 0;

    img {
      -webkit-text-size-adjust: 100%;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
      font-size: 14px;
      line-height: 1.428571429;
      font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
      color: #333;
      box-sizing: border-box;
      border: 0;
      vertical-align: middle;
      max-height: 999999px;
      height: auto;
      max-width: 100%;
      display: block;
      margin-left: auto;
      margin-right: auto;
    }
  }

  .search-noUiSlider {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-size: 14px;
    line-height: 1.428571429;
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    box-sizing: border-box;
    max-height: 999999px;
  }

  .noUiSlider.noUi-target.noUi-ltr.noUi-horizontal.noUi-background {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-size: 14px;
    line-height: 1.428571429;
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    max-height: 999999px;
    -webkit-user-select: none;
    box-sizing: border-box;
    box-shadow:
      inset 0 1px 1px #f0f0f0,
      0 3px 6px -5px #bbb;
    background: none repeat scroll 0 0 #e1e1e1;
    border: 0 none;
    border-radius: 10px;
    height: 6px;

    .noUi-base {
      -webkit-text-size-adjust: 100%;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
      font-size: 14px;
      line-height: 1.428571429;
      font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
      color: #333;
      max-height: 999999px;
      -webkit-user-select: none;
      box-sizing: border-box;
      width: 100%;
      height: 100%;
      position: relative;
      background: #183E40;

      .ui-slider-four {
        -webkit-text-size-adjust: 100%;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        font-size: 14px;
        line-height: 1.428571429;
        font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
        color: #333;
        max-height: 999999px;
        -webkit-user-select: none;
        box-sizing: border-box;
        padding: 0;
        margin: 0;
        list-style: none;

        .fore0,
        .fore1,
        .fore2,
        .fore3,
        .fore4,
        .fore5,
        .fore6,
        .fore7 {
          list-style: none;
          box-sizing: border-box;
          position: absolute;
          width: 20px;
          height: 7px;
          border-left: 1px solid white;
          z-index: 2;
          cursor: pointer;

          &::before {
            content: "hello ",
            width: 10px,
            height: 10px,
          }
        }

        .fore0 {
          left: 0;
        }

        .fore1 {
          left: 14.28571429%;
        }

        .fore2 {
          left: 28.57142857%;
        }

        .fore3 {
          left: 42.85714286%;
        }

        .fore4 {
          left: 57.14285714%;
        }

        .fore5 {
          left: 71.42857143%;
        }

        .fore6 {
          left: 85.71428571%;
        }

        .fore7 {
          left: 100%;
        }
      }

      .noUi-origin {
        -webkit-text-size-adjust: 100%;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        font-size: 14px;
        line-height: 1.428571429;
        font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
        color: #333;
        max-height: 999999px;
        -webkit-user-select: none;
        box-sizing: border-box;
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        border-radius: 2px;
        left: 0%;

        .noUi-handle.noUi-handle-lower {
          -webkit-text-size-adjust: 100%;
          -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
          font-size: 14px;
          line-height: 1.428571429;
          font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
          color: #333;
          max-height: 999999px;
          -webkit-user-select: none;
          box-sizing: border-box;
          top: -6px;
          border: none;
          height: 20px;
          width: 20px;
          margin: -1px 0 0 -10px;
          border-radius: 20px;
          box-shadow: none;
          background: #183E40;
          left: 0;
          z-index: 3;
          cursor: pointer;
          position: relative;

          &::before {
            left: -5px;
            display: block;
            background: transparent;
            width: 40px;
            height: 40px;
            top: -50%;
            content: '';
            position: absolute;
          }
        }
      }
    }
  }

  .modal-clarity-data.modal-slider-four-data.clearfix {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-size: 14px;
    line-height: 1.428571429;
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    box-sizing: border-box;
    margin-top: 0;
    margin-bottom: 10px;
    max-height: 999999px;
    padding: 10px 0 0;
    text-transform: capitalize;

    &::before,
    &::after {
      display: table;
      content: ' ';
    }

    &::after {
      clear: both;
    }

    li {
      -webkit-text-size-adjust: 100%;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
      font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
      color: #333;
      text-transform: capitalize;
      box-sizing: border-box;
      max-height: 999999px;
      float: left;
      list-style: none;
      font-size: 12px;
      line-height: 1em;
      width: 14.28571429%;

      &.last {
        margin-left: -14.28571429%;

        span {
          left: auto;
          width: 60px;
          right: -30px;
          float: right;
        }
      }

      span {
        -webkit-text-size-adjust: 100%;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
        color: #333;
        text-transform: capitalize;
        list-style: none;
        font-size: 12px;
        line-height: 1em;
        box-sizing: border-box;
        max-height: 999999px;
        display: block;
        position: relative;
        left: -50%;
        text-align: center;
        white-space: nowrap;
      }
    }
  }

  .modal_cuts_slider.modal-slider-tooltip {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-size: 14px;
    line-height: 1.428571429;
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    box-sizing: border-box;
    max-height: 999999px;
  }

  .tooltip.bottom {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    box-sizing: border-box;
    max-height: 999999px;
    z-index: 1030;
    font-size: 12px;
    line-height: 1.4;
    visibility: visible;
    margin-top: 3px;
    display: block;
    opacity: 1;
    position: relative;
    padding: 10px 0 0;
  }

  .tooltip-arrow {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    font-size: 12px;
    line-height: 1.4;
    visibility: visible;
    box-sizing: border-box;
    max-height: 999999px;
    position: absolute;
    width: 0;
    height: 0;
    border-color: transparent;
    border-style: solid;
    top: 0;
    border-bottom-color: #183E40;
    border-width: 0 10px 10px;
    margin-left: -10px;
    left: 10px;

    &.hidden {
      display: none !important;
      visibility: hidden !important;
    }
  }

  .tooltip-inner {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    line-height: 1.4;
    visibility: visible;
    box-sizing: border-box;
    max-height: 999999px;
    text-decoration: none;
    max-width: 100%;
    text-align: center;
    background-color: #183E40;
    clear: both;
    height: 100%;
    padding: 0.5em 1em;
    min-height: 68px;
    display: flex;
    align-items: center;
    color: white;
    font-size: 13px;
    border-radius: 0;

    p {
      width: 100%;
      -webkit-text-size-adjust: 100%;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
      font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
      line-height: 1.4;
      visibility: visible;
      text-align: center;
      margin: 0;
      color: white;
      font-size: 13px;
      box-sizing: border-box;
      max-height: 999999px;
    }
  }
`;

export default function DiamondClarityDiagram({
  contentPosition,
  mediaAlignment,
  optionalLargeHeader,
  anchorLinkID,
  margin = false,
  content,
  actions,
}: IDiamondClarityDiagramProps) {
  const [activeCut, setActiveCut] = useState<number>(0);
  const diamondCuts = [
    {
      title: 'SI2',
      description:
        'Slightly included 2. Inclusions are visible under 10x magnification and may be visible with an unaided eye. More inclusions than SI1.',
      imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-clarity/clarity-scale-1-SI2.png',
    },
    {
      title: 'SI1',
      description:
        'Slightly included 1. Inclusions are visible under 10x magnification, but may not be very visible with an unaided eye.',
      imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-clarity/clarity-scale-2-SI1.png',
    },
    {
      title: 'VS2',
      description:
        'Very slightly included 2. Difficult to see inclusions under 10x magnification. Typically cannot see inclusions with the naked eye. Slightly more inclusions than VS1.',
      imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-clarity/clarity-scale-3-VS2.png',
    },
    {
      title: 'VS1',
      description:
        'Very slightly included 1. Difficult to see inclusions under 10x magnification. Typically cannot see inclusions with the naked eye.',
      imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-clarity/clarity-scale-4-VS1.png',
    },
    {
      title: 'VVS2',
      description:
        'Very very slightly included 2. Very difficult to see under 10x magnification. Slightly more inclusions than VVS1.',
      imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-clarity/clarity-scale-5-VVS2.png',
    },
    {
      title: 'VVS1',
      description: 'Very very slightly included 1. Very difficult to see under 10x magnification.',
      imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-clarity/clarity-scale-6-VVS1.png',
    },
    {
      title: 'IF',
      description: 'Internally Flawless. No internal flaws.',
      imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-clarity/clarity-scale-7-IF.png',
    },
    {
      title: 'FL',
      description: 'Flawless with no internal or external flaws. Extremely rare and valuable.',
      imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-clarity/clarity-scale-8-FL.png',
    },
  ];
  const formattedActions = actions?.map(action => ({
    ...action,
    // @ts-ignore
    color: action.color === 'dark' ? 'dark-green' : 'white',
  }));

  return (
    <div
      {...getAnchorId(anchorLinkID)}
      className={classNames('tw-w-[85%] tablet:tw-w-[85%] tw-max-w-[1440px] tw-mx-auto', getComponentMargin(margin))}
    >
      {optionalLargeHeader?.content && (
        <Title
          component={optionalLargeHeader?.titleHTMLTag || 'h2'}
          weight={optionalLargeHeader?.titleWeight}
          size={optionalLargeHeader?.titleSize}
          alignment="center"
        >
          {optionalLargeHeader?.content}
        </Title>
      )}
      <div
        className={classNames(
          'tw-flex tw-items-center lg:tw-mx-auto tw-flex-col lg:tw-flex-row tw-max-w-[1172px] tw-w-full lg:tw-w-full tw-gap-x-[64px] lg:tw-max-w-[1440px]',
          {
            'lg:tw-flex-row-reverse': contentPosition === 'right',
            'lg:tw-items-start ': mediaAlignment === 'start',
          }
        )}
      >
        <div className="tw-w-full tw-flex tw-justify-center tw-mb-6 lg:tw-mb-0 lg:tw-w-1/2">
          <Container>
            <div className="col-md-6 col-md-pull-6">
              <div className="search-noUiSlider-wrap">
                <div className="clarity-scale">
                  <div className="tw-relative tw-aspect-[270/173] tw-max-w-[270px] img-responsive center-block">
                    <Image
                      src={diamondCuts[activeCut].imageURL}
                      fill
                      alt={`Diamond clarity characteristics - ${diamondCuts[activeCut].title}`}
                    />
                  </div>
                </div>
                <div id="modal_cuts" className="search-noUiSlider">
                  <div
                    id="modal_cuts_slider"
                    className="noUiSlider noUi-target noUi-ltr noUi-horizontal noUi-background"
                  >
                    <div className="noUi-base">
                      <div className="noUi-origin" style={{ left: 0 }}>
                        <div
                          className="noUi-handle noUi-handle-lower"
                          style={{ left: `${activeCut * 14.285}%`, transition: 'left 0.3s, top 0.3s' }}
                        />
                      </div>
                      <ol className="ui-slider-four">
                        <li className="fore0" onClick={() => setActiveCut(0)} />
                        <li className="fore1" onClick={() => setActiveCut(1)} />
                        <li className="fore2" onClick={() => setActiveCut(2)} />
                        <li className="fore3" onClick={() => setActiveCut(3)} />
                        <li className="fore4" onClick={() => setActiveCut(4)} />
                        <li className="fore5" onClick={() => setActiveCut(5)} />
                        <li className="fore6" onClick={() => setActiveCut(6)} />
                        <li className="fore7" onClick={() => setActiveCut(7)} />
                      </ol>
                    </div>
                  </div>
                  <ul className="modal-clarity-data modal-slider-four-data clearfix">
                    {diamondCuts.map((cut, index) => (
                      <li
                        key={cut.title.replace(' ', '-')}
                        className={
                          diamondCuts[7].title === cut.title
                            ? 'last hover:tw-cursor-pointer tw-cursor-pointer'
                            : 'tw-cursor-pointer hover:tw-cursor-pointer'
                        }
                        onClick={() => setActiveCut(index)}
                      >
                        <span>{cut.title}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="modal_cuts_slider modal-slider-tooltip">
                    <div role="tooltip" className="tooltip bottom">
                      <div className={`tooltip-arrow ${activeCut !== 0 ? 'hidden' : ''}`} style={{ left: '10px' }} />
                      <div className={`tooltip-arrow ${activeCut !== 1 ? 'hidden' : ''}`} style={{ left: '14.285%' }} />
                      <div className={`tooltip-arrow ${activeCut !== 2 ? 'hidden' : ''}`} style={{ left: '28.571%' }} />
                      <div className={`tooltip-arrow ${activeCut !== 3 ? 'hidden' : ''}`} style={{ left: '42.857%' }} />
                      <div
                        className={`tooltip-arrow ${activeCut !== 4 ? 'hidden' : ''}`}
                        style={{ left: '57.142%', marginLeft: '-10px' }}
                      />
                      <div className={`tooltip-arrow ${activeCut !== 5 ? 'hidden' : ''}`} style={{ left: '71.428%' }} />
                      <div className={`tooltip-arrow ${activeCut !== 6 ? 'hidden' : ''}`} style={{ left: '85.714%' }} />
                      <div
                        className={`tooltip-arrow ${activeCut !== 7 ? 'hidden' : ''}`}
                        style={{ left: '100%', marginLeft: '-20px' }}
                      />
                      <div className="tooltip-inner">
                        <p>{diamondCuts[activeCut].description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>

        <div className="tw-w-full lg:tw-w-1/2 tw-max-w-[1172px] lg:tw-mt-0">
          <RichText content={content} />

          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 md:tw-flex-row tw-gap-2 md:tw-gap-4 tw-w-full tw-mt-12">
            {formattedActions?.slice(0, 4).map((action, i) => {
              const { label, color, type } = action;

              if (action.actions) {
                const { actions: subActions } = action;
                return (
                  <DropdownButton
                    key={i}
                    label={label}
                    options={subActions}
                    // @ts-ignore
                    color={color}
                    type={type}
                    className={beTwMerge(
                      `tw-basis-[calc(50%-8px)] tw-w-full tw-text-center tw-max-w-none [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis`
                    )}
                  />
                );
              }
              const { onClick, href } = action;
              return (
                <Button
                  className={beTwMerge(
                    `tw-basis-[calc(50%-8px)] tw-max-w-none tw-w-full tw-text-center tw-whitespace-nowrap tw-text-ellipsis md:tw-overflow-hidden`
                  )}
                  ariaLabel={moreDescriptiveAriaLabel(optionalLargeHeader?.content, label)}
                  key={i}
                  label={label}
                  // @ts-ignore
                  color={color}
                  type={type}
                  href={href}
                  onClick={onClick}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
